import React, { useState } from 'react';
import styled, { keyframes, css, createGlobalStyle } from 'styled-components';

// ─── Data ────────────────────────────────────────────────────────────────────

const US_PARKS = [
  { id: 1,  name: "Yellowstone",        state: "WY",    emoji: "🌋", tagline: "Hot, steamy & always erupting." },
  { id: 2,  name: "Death Valley",       state: "CA",    emoji: "🥵", tagline: "The original thirst trap." },
  { id: 3,  name: "Mount Rainier",      state: "WA",    emoji: "🐾", tagline: "Cougar country. You've been warned." },
  { id: 4,  name: "Joshua Tree",        state: "CA",    emoji: "🌵", tagline: "Prickly outside. Bare inside." },
  { id: 5,  name: "Multnomah Falls",    state: "OR",    emoji: "💦", tagline: "Always wet. Always." },
  { id: 6,  name: "Appalachian Trail",  state: "Multi", emoji: "🕳️", tagline: "Caves: dark, deep & dripping." },
  { id: 7,  name: "Grand Canyon",       state: "AZ",    emoji: "😮", tagline: "Deep, wide & breathtaking." },
  { id: 8,  name: "Zion",               state: "UT",    emoji: "🪨", tagline: "Come get in our Narrows." },
  { id: 9,  name: "Arches",             state: "UT",    emoji: "🌀", tagline: "Openings in all the right places." },
  { id: 10, name: "Bryce Canyon",       state: "UT",    emoji: "🍑", tagline: "Full of hoodoos." },
  { id: 11, name: "Glacier",            state: "MT",    emoji: "💧", tagline: "Dripping for you. Slowly." },
  { id: 12, name: "Acadia",             state: "ME",    emoji: "🌊", tagline: "Rocky, rugged & worth every inch." },
  { id: 13, name: "Great Smoky Mtns",   state: "TN/NC", emoji: "💨", tagline: "Smoky, steamy & mysterious." },
  { id: 14, name: "Crater Lake",        state: "OR",    emoji: "🌑", tagline: "Deep, dark & bottomless." },
  { id: 15, name: "Olympic",            state: "WA",    emoji: "🌿", tagline: "Lush, wild & always wet." },
  { id: 16, name: "Rocky Mountain",     state: "CO",    emoji: "🏔️", tagline: "High altitude. Big peaks." },
  { id: 17, name: "Sequoia",            state: "CA",    emoji: "🌲", tagline: "Enormous, towering wood." },
  { id: 18, name: "Everglades",         state: "FL",    emoji: "🐊", tagline: "Swampy in all the right ways." },
  { id: 19, name: "Shenandoah",         state: "VA",    emoji: "🌄", tagline: "Backdoor access available." },
  { id: 20, name: "Isle Royale",        state: "MI",    emoji: "🏝️", tagline: "Private. Remote. Exclusive content." },
];

const INTL_PARKS = [
  { id: 21, name: "Banff",              state: "Canada",    emoji: "🇨🇦", tagline: "Glacially hot for our neighbors." },
  { id: 22, name: "Jasper",             state: "Canada",    emoji: "🌌", tagline: "Dark skies. Very dark. Come see." },
  { id: 23, name: "Cairngorms",         state: "Scotland",  emoji: "🏴󠁧󠁢󠁳󠁣󠁴󠁿", tagline: "Highland and ready to mingle." },
  { id: 24, name: "Fiordland",          state: "New Zealand", emoji: "🌧️", tagline: "Wettest place on earth. You're welcome." },
];

const parks = [...US_PARKS, ...INTL_PARKS];

// ─── Print global styles ──────────────────────────────────────────────────────

const PrintStyles = createGlobalStyle`
  @media print {
    @page {
      size: letter landscape;
      margin: 0.3in;
    }

    * { animation: none !important; transition: none !important; }

    body > * { visibility: hidden !important; }

    #only-parks-card,
    #only-parks-card * { visibility: visible !important; }

    #only-parks-card {
      position: fixed !important;
      inset: 0 !important;
      margin: 0 !important;
      padding: 14px 18px !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      background: #f5edd8 !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      display: flex !important;
      flex-direction: column !important;
    }

    #screen-only-content { display: none !important; visibility: hidden !important; }

    #print-only-grid {
      display: grid !important;
      visibility: visible !important;
      grid-template-columns: repeat(6, 1fr) !important;
      gap: 7px !important;
      flex: 1 !important;
    }

    #only-parks-print-credit { display: block !important; }
  }
`;

// ─── Animations ──────────────────────────────────────────────────────────────

const stampDrop = keyframes`
  0%   { transform: scale(2.5) rotate(-20deg); opacity: 0; }
  55%  { transform: scale(0.88) rotate(4deg);  opacity: 1; }
  75%  { transform: scale(1.06) rotate(-2deg); opacity: 1; }
  100% { transform: scale(1)    rotate(0deg);  opacity: 1; }
`;

const inkSpread = keyframes`
  0%   { opacity: 0; transform: scale(0.6); }
  100% { opacity: 1; transform: scale(1); }
`;

const floatBadge = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-6px); }
`;

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

// ─── Styled Components ────────────────────────────────────────────────────────

const PageContainer = styled.div`
  width: 100%;
  background: #0a1f0f;
  min-height: 100vh;
`;

/* ── Hero ── */
const HeroSection = styled.section`
  background: linear-gradient(160deg, #0a1f0f 0%, #12372a 60%, #0a1f0f 100%);
  padding: 70px 20px 50px;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '🌲🌿🍃🌲🌿🍃🌲🌿🍃🌲🌿🍃🌲🌿🍃🌲🌿🍃🌲🌿🍃🌲🌿🍃';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    font-size: 28px;
    letter-spacing: 4px;
    opacity: 0.12;
    white-space: nowrap;
    overflow: hidden;
    pointer-events: none;
  }

  @media print { display: none; }
`;

const LogoRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 18px;
  margin-bottom: 20px;
  animation: ${floatBadge} 3s ease-in-out infinite;
`;

const OPBadge = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00c9a7 0%, #00b4d8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 900;
  color: white;
  letter-spacing: -3px;
  box-shadow: 0 0 30px rgba(0, 180, 216, 0.6), 0 4px 15px rgba(0,0,0,0.4);
  flex-shrink: 0;
`;

const BrandTitle = styled.h1`
  font-size: 56px;
  font-weight: 900;
  color: white;
  letter-spacing: -1px;
  line-height: 1;
  margin: 0;

  span {
    background: linear-gradient(90deg, #00c9a7, #00b4d8);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  @media (max-width: 768px) { font-size: 38px; }
`;

const Tagline = styled.p`
  color: rgba(255, 255, 255, 0.75);
  font-size: 20px;
  margin: 10px 0 6px;
  font-style: italic;
`;

const SubTagline = styled.p`
  color: rgba(255, 255, 255, 0.55);
  font-size: 14px;
  margin: 0 auto 30px;
  max-width: 500px;
  line-height: 1.6;
`;

const ProgressRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 14px;
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 50px;
  padding: 10px 22px;
  color: white;
  font-size: 15px;

  strong {
    color: #00c9a7;
    font-size: 20px;
  }
`;

const ProgressBar = styled.div`
  width: 160px;
  height: 8px;
  background: rgba(255,255,255,0.12);
  border-radius: 4px;
  overflow: hidden;

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${props => props.$pct}%;
    background: linear-gradient(90deg, #00c9a7, #00b4d8);
    border-radius: 4px;
    transition: width 0.4s ease;
  }
`;

/* ── Card section wrapper ── */
const CardSection = styled.section`
  padding: 40px 20px 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;

  @media print { padding: 0; }
`;

const PrintButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #00c9a7, #00b4d8);
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 15px rgba(0, 180, 216, 0.4);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 180, 216, 0.6);
  }

  @media print { display: none !important; }
`;

/* ── Punch Card ── */
const PunchCard = styled.div`
  background: #f5edd8;
  border-radius: 18px;
  padding: 36px 32px 28px;
  max-width: 980px;
  width: 100%;
  box-shadow:
    0 20px 60px rgba(0,0,0,0.6),
    0 0 0 1px rgba(255,255,255,0.05),
    inset 0 1px 0 rgba(255,255,255,0.9);
  position: relative;

  /* Perforated top and bottom edges */
  &::before,
  &::after {
    content: '';
    display: block;
    position: absolute;
    left: 24px;
    right: 24px;
    height: 0;
    border-top: 2px dashed rgba(139,100,60,0.25);
  }
  &::before { top: 16px; }
  &::after  { bottom: 16px; }

  @media (max-width: 600px) {
    padding: 24px 16px 20px;
    border-radius: 12px;
  }

  @media print {
    border-radius: 0;
    box-shadow: none;
    padding: 24px;
    max-width: 100%;
  }
`;

const CardHeader = styled.div`
  text-align: center;
  margin-bottom: 28px;

  @media print { margin-bottom: 10px; }
`;

const CardTitle = styled.h2`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: #7b5e3a;
  margin-bottom: 4px;
`;

const CardSubtitle = styled.p`
  font-size: 11px;
  color: #a0845c;
  letter-spacing: 2px;
  text-transform: uppercase;
`;

const SectionLabel = styled.div`
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #a0845c;
  margin: 16px 0 8px;
  text-align: left;
  padding-left: 4px;
  border-top: 1px dashed rgba(139,100,60,0.2);
  padding-top: 14px;

  &:first-of-type {
    border-top: none;
    padding-top: 0;
    margin-top: 0;
  }
`;

const StampGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 14px;

  @media (max-width: 800px) { grid-template-columns: repeat(4, 1fr); }
  @media (max-width: 600px) { grid-template-columns: repeat(3, 1fr); gap: 10px; }
  @media (max-width: 400px) { grid-template-columns: repeat(2, 1fr); }

  @media print {
    grid-template-columns: repeat(5, 1fr);
    gap: 10px;
  }
`;

const IntlGrid = styled(StampGrid)`
  grid-template-columns: repeat(4, 1fr);

  @media (max-width: 600px) { grid-template-columns: repeat(2, 1fr); }
  @media print { grid-template-columns: repeat(4, 1fr); }
`;

/* Hidden on screen, becomes the print layout grid */
const PrintUnifiedGrid = styled.div`
  display: none;
`;

const StampCell = styled.div`
  aspect-ratio: 1;
  border-radius: 50%;
  border: 2.5px dashed ${props => props.$punched ? '#1a5c30' : '#c8a97e'};
  background: ${props => props.$punched
    ? 'radial-gradient(circle, #1e7a3e 0%, #145228 100%)'
    : 'rgba(255,255,255,0.55)'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 10px 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
  user-select: none;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;

  &:hover {
    box-shadow: 0 4px 20px rgba(0,0,0,0.18);
    border-color: ${props => props.$punched ? '#2a8f45' : '#a07850'};
    transform: scale(1.04);
  }

  &:active { transform: scale(0.97); }

  ${props => props.$punched && css`
    &::before {
      content: '';
      position: absolute;
      inset: -2px;
      border-radius: 50%;
      background: radial-gradient(circle at 50% 50%, rgba(30,122,62,0.18) 0%, transparent 70%);
      animation: ${inkSpread} 0.3s ease forwards;
    }
  `}

  @media print {
    border-width: 2px;
    cursor: default;
    &:hover { transform: none; box-shadow: none; }
  }
`;

const StampContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  z-index: 1;
`;

const StampEmoji = styled.div`
  font-size: 22px;
  line-height: 1;
  @media (max-width: 600px) { font-size: 18px; }
  @media print { font-size: 18px; }
`;

const StampName = styled.div`
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${props => props.$punched ? 'rgba(255,255,255,0.95)' : '#5a3e28'};
  line-height: 1.2;
  max-width: 80px;
  @media (max-width: 600px) { font-size: 8px; }
`;

const StampState = styled.div`
  font-size: 8px;
  color: ${props => props.$punched ? 'rgba(255,255,255,0.6)' : '#a07850'};
  letter-spacing: 0.5px;
`;

const StampOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  animation: ${stampDrop} 0.35s cubic-bezier(0.22, 1, 0.36, 1) forwards;
  z-index: 2;
`;

const StampMark = styled.div`
  font-size: 28px;
  line-height: 1;
  transform: rotate(-8deg);
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
  @media (max-width: 600px) { font-size: 22px; }
`;

const StampText = styled.div`
  font-size: 7px;
  font-weight: 900;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.7);
  transform: rotate(-8deg);
  margin-top: 2px;
`;

/* ── Card footer ── */
const CardFooter = styled.div`
  margin-top: 22px;
  text-align: center;
  border-top: 1.5px dashed rgba(139,100,60,0.2);
  padding-top: 14px;

  @media print { margin-top: 8px; padding-top: 8px; }
`;

const RewardText = styled.p`
  font-size: 11px;
  color: #a0845c;
  letter-spacing: 1.5px;
  text-transform: uppercase;
`;

const CompleteMessage = styled.div`
  margin-top: 8px;
  font-size: 18px;
  font-weight: 700;
  color: #1a5c30;
  animation: ${floatBadge} 2s ease-in-out infinite;
  @media print { animation: none; }
`;

/* ── Print credit (hidden on screen, visible in print) ── */
const PrintCredit = styled.div`
  display: none;
  margin-top: 16px;
  text-align: center;
  font-size: 9px;
  color: #a0845c;
  letter-spacing: 2px;
  text-transform: uppercase;

  @media print { display: block !important; }
`;

/* ── Bottom section ── */
const BottomSection = styled.section`
  background: #0a1f0f;
  padding: 30px 20px 50px;
  text-align: center;
  @media print { display: none; }
`;

const ShimmerHint = styled.p`
  font-size: 13px;
  color: rgba(255,255,255,0.55);
  margin-top: 14px;
  letter-spacing: 0.5px;

  background: linear-gradient(
    90deg,
    rgba(255,255,255,0.35) 0%,
    rgba(255,255,255,0.75) 50%,
    rgba(255,255,255,0.35) 100%
  );
  background-size: 400px 100%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${shimmer} 3s linear infinite;
`;

const ResetButton = styled.button`
  margin-top: 20px;
  background: transparent;
  border: 1px solid rgba(255,255,255,0.2);
  color: rgba(255,255,255,0.45);
  font-size: 12px;
  padding: 8px 20px;
  border-radius: 20px;
  cursor: pointer;
  letter-spacing: 1px;
  text-transform: uppercase;
  transition: all 0.2s;

  &:hover {
    border-color: rgba(0,201,167,0.5);
    color: rgba(0,201,167,0.8);
  }
`;

const Disclaimer = styled.p`
  color: rgba(255, 255, 255, 0.62);
  font-size: 13px;
  max-width: 580px;
  margin: 24px auto 0;
  line-height: 1.8;
  font-style: italic;
`;

// ─── Stamp helper ─────────────────────────────────────────────────────────────

function Stamp({ park, isPunched, onToggle }) {
  return (
    <StampCell $punched={isPunched} onClick={() => onToggle(park.id)} title={park.tagline}>
      <StampContent style={{ opacity: isPunched ? 0.15 : 1, transition: 'opacity 0.2s' }}>
        <StampEmoji>{park.emoji}</StampEmoji>
        <StampName $punched={isPunched}>{park.name}</StampName>
        <StampState $punched={isPunched}>{park.state}</StampState>
      </StampContent>
      {isPunched && (
        <StampOverlay>
          <StampMark>✅</StampMark>
          <StampText>Visited</StampText>
        </StampOverlay>
      )}
    </StampCell>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

function SpicyParks() {
  const [punched, setPunched] = useState(new Set());

  const toggle = (id) => {
    setPunched(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const reset = () => setPunched(new Set());

  const count = punched.size;
  const total = parks.length;
  const pct   = Math.round((count / total) * 100);
  const done  = count === total;

  return (
    <PageContainer>
      <PrintStyles />

      <HeroSection>
        <LogoRow>
          <OPBadge>OP</OPBadge>
          <BrandTitle>Only <span>Parks</span></BrandTitle>
        </LogoRow>

        <Tagline>"Spicy nature content, for conservation."</Tagline>
        <SubTagline>
          A TikTok movement started by Yellowstone, because if posting
          steamy geyser footage keeps the parks funded, we are all in.
        </SubTagline>

        <ProgressRow>
          <strong>{count}</strong>
          <span>/ {total} parks collected</span>
          <ProgressBar $pct={pct} />
          <span>{pct}%</span>
        </ProgressRow>
      </HeroSection>

      <CardSection>
        <PrintButton onClick={() => window.print()}>
          🖨️ Print Your Punch Card
        </PrintButton>

        <PunchCard id="only-parks-card">
          <CardHeader>
            <CardTitle>Only Parks — Loyalty Punch Card</CardTitle>
            <CardSubtitle>Tap each park to collect your stamp · Leave no park behind</CardSubtitle>
          </CardHeader>

          {/* Screen layout: two labeled sections */}
          <div id="screen-only-content">
            <SectionLabel>United States</SectionLabel>
            <StampGrid>
              {US_PARKS.map(park => (
                <Stamp key={park.id} park={park} isPunched={punched.has(park.id)} onToggle={toggle} />
              ))}
            </StampGrid>

            <SectionLabel>International Friends</SectionLabel>
            <IntlGrid>
              {INTL_PARKS.map(park => (
                <Stamp key={park.id} park={park} isPunched={punched.has(park.id)} onToggle={toggle} />
              ))}
            </IntlGrid>
          </div>

          {/* Print layout: unified 6-column grid, all 24 parks */}
          <PrintUnifiedGrid id="print-only-grid">
            {parks.map(park => (
              <Stamp key={`p-${park.id}`} park={park} isPunched={punched.has(park.id)} onToggle={() => {}} />
            ))}
          </PrintUnifiedGrid>

          <CardFooter>
            <RewardText>
              Collect all {total} stamps · Earn bragging rights + a lifetime of dirt in your boots
            </RewardText>
            {done && (
              <CompleteMessage>
                🌲 CARD COMPLETE — You absolute park legend. 🌲
              </CompleteMessage>
            )}
          </CardFooter>

          <PrintCredit id="only-parks-print-credit">
            Generated at zumosoft.com/only-parks · Support your parks at nps.gov
          </PrintCredit>
        </PunchCard>
      </CardSection>

      <BottomSection>
        <ShimmerHint>Tap any stamp to punch it. Tap again to unvisit your regrets.</ShimmerHint>

        <ResetButton onClick={reset}>Reset Card</ResetButton>

        <Disclaimer>
          Only Parks is a fan tribute to the #ParkTok trend. No national parks were harmed in
          the making of this page. All taglines are nature facts presented in a deeply
          irresponsible context. "Enormous, towering wood" refers to sequoia trees. We will
          not be taking further questions. Support your national parks at <a href="https://www.nps.gov" target="_blank" rel="noopener noreferrer" style={{color: 'rgba(0,201,167,0.8)', textDecoration: 'underline'}}>nps.gov</a>.
        </Disclaimer>
      </BottomSection>
    </PageContainer>
  );
}

export default SpicyParks;
